import { useMemo, useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Eye, ArrowLeftRight, History, Trash2, Edit2 } from "lucide-react";
import EditIcon from "@material-ui/icons/Edit";
import MaterialTable from "material-table";
import type { Column } from "material-table";
import { materialTableES } from "../../../../services/materialTableES";
import { Badge } from "../../../../components/Badge";
import { RowActionsMenu } from "../../../../components/RowActionsMenu/RowActionsMenu";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import styles from "./ProductsTable.module.scss";
import { ProductDetails } from "../ProductDetails/ProductDetails";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import {
  useGetInventory,
  useTransferStock,
  useDeleteProduct,
  useUpdateProductPrice,
  useUpdateProductStock,
} from "../../../../utils/react-query/queryInventory/queries";
import { useMyPermissions } from "../../../../utils/react-query/queryRbac/queries";
import {
  type SalesProduct,
  type InventoryTableRow,
  type StorageAddressRef,
  type TransferPayload,
  type UpdatePricePayload,
  type UpdateStockPayload,
  type StockByLocation,
} from "./types";
import { StockTransferModal } from "../StockTransferModal/StockTransferModal";
import { TransferHistoryModal } from "../TransferHistoryModal/TransferHistoryModal";
import { successToast, errorToast } from "../../../../services/toasts";
import { useFilterStore } from "../../../../supplierEcommerce/store";

export type ProductStatus = "in-stock" | "low-stock" | "out-of-stock";

const INVENTORY_PAGE_SIZE = 50;

export const getProductStatus = (unitsAvailable: number): ProductStatus => {
  if (unitsAvailable <= 0) return "out-of-stock";
  if (unitsAvailable < 10) return "low-stock";
  return "in-stock";
};

/** Pure filter function — testable independently of React lifecycle. */
export function filterInventoryProducts(
  products: InventoryTableRow[],
  searchQuery: string,
  filter: Filter
): InventoryTableRow[] {
  const normalizedQuery = searchQuery.toLowerCase();
  return products.filter((product) => {
    const matchesSearch =
      !searchQuery ||
      product.name.toLowerCase().includes(normalizedQuery) ||
      product.line.toLowerCase().includes(normalizedQuery);

    const matchesBrand =
      filter.brand.length === 0 || filter.brand.includes(product.brand ?? "");
    const matchesLine =
      filter.line.length === 0 || filter.line.includes(product.line);
    const selectedCategories = filter.category ?? [];
    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.some((category) =>
        (product.categories ?? []).includes(category)
      );

    const [minUnitPrice, maxUnitPrice] = filter.unitPrice ?? [null, null];
    const matchesUnitPrice =
      (minUnitPrice === null && maxUnitPrice === null) ||
      (product.unit_price != null &&
        (minUnitPrice === null || product.unit_price >= minUnitPrice) &&
        (maxUnitPrice === null || product.unit_price <= maxUnitPrice));

    return (
      matchesSearch &&
      matchesBrand &&
      matchesLine &&
      matchesCategory &&
      matchesUnitPrice
    );
  });
}

export interface StockMetrics {
  physicalStock: number;
  reservedStock: number;
  availableStock: number;
}

/** Returns the three inventory values shown to operators for one location. */
export function getStockMetrics(stock?: StockByLocation): StockMetrics {
  const physicalStock = Number(stock?.quantity ?? 0);
  const reservedStock = Number(stock?.reserved_stock ?? 0);
  const availableStock = Number(
    stock?.available_stock ?? physicalStock - reservedStock
  );

  return { physicalStock, reservedStock, availableStock };
}

/** Sums physical, reserved, and available values across all locations. */
export function getInventoryTotals(
  stockByLocation: StockByLocation[]
): StockMetrics {
  return stockByLocation.reduce(
    (totals, stock) => {
      const metrics = getStockMetrics(stock);
      return {
        physicalStock: totals.physicalStock + metrics.physicalStock,
        reservedStock: totals.reservedStock + metrics.reservedStock,
        availableStock: totals.availableStock + metrics.availableStock,
      };
    },
    { physicalStock: 0, reservedStock: 0, availableStock: 0 }
  );
}

const getStatusBadge = (status: ProductStatus) => {
  switch (status) {
    case "in-stock":
      return { variant: "success" as const, label: "En Stock" };
    case "low-stock":
      return { variant: "warning" as const, label: "Stock Bajo" };
    case "out-of-stock":
      return { variant: "danger" as const, label: "Agotado" };
  }
};

/**
 * Builds dynamic MaterialTable columns — one per storage address.
 * Pure function: same inputs always produce the same column definitions.
 *
 * @param storageAddresses - list of locations from GET /v2/inventory
 * @param options.canEditQuantity - whether to render the inline edit affordance
 * @param options.editingCell - currently-edited (productId, addressId, value) — renders an inline input
 * @param options.onEditQuantity - called when the user clicks the edit icon
 * @param options.onChangeQuantity - called when the inline input value changes
 * @param options.onSubmitQuantity - called when the user confirms the inline input
 * @param options.onCancelQuantity - called when the user cancels the inline input
 */
export function buildInventoryColumns(
  storageAddresses: StorageAddressRef[],
  options: {
    canEditQuantity: boolean;
    editingCell: { productId: number; addressId: number; value: string } | null;
    onEditQuantity: (product: InventoryTableRow, address: StorageAddressRef) => void;
    onChangeQuantity: (value: string) => void;
    onSubmitQuantity: () => void;
    onCancelQuantity: () => void;
  }
): Column<InventoryTableRow>[] {
  const {
    canEditQuantity,
    editingCell,
    onEditQuantity,
    onChangeQuantity,
    onSubmitQuantity,
    onCancelQuantity,
  } = options;

  return storageAddresses.map((address) => ({
    title: address.name,
    field: `stock_${address.id}`,
    type: "numeric" as const,
    editable: "never" as const,
    render: (rowData: InventoryTableRow) => {
      const stock = rowData.stockByLocation.find(
        (s) => s.storage_address_id === address.id
      );
      const metrics = getStockMetrics(stock);

      const isEditing =
        canEditQuantity &&
        editingCell?.productId === rowData.id &&
        editingCell?.addressId === address.id;

      if (isEditing) {
        return (
          <input
            type="number"
            min={0}
            step={1}
            autoFocus
            className={styles.quantityInput}
            value={editingCell!.value}
            onChange={(e) => onChangeQuantity(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSubmitQuantity();
              else if (e.key === "Escape") onCancelQuantity();
            }}
            onBlur={onCancelQuantity}
            data-testid="quantity-input"
          />
        );
      }

       const stockMetrics = (
         <span className={styles.stockMetrics} aria-label={`${address.name} stock`}>
           <span>Físico: {metrics.physicalStock}</span>
           <span>Reservado: {metrics.reservedStock}</span>
           <span>Disponible: {metrics.availableStock}</span>
         </span>
       );

       if (!canEditQuantity) return stockMetrics;

       return (
         <span className={styles.stockCell}>
           {stockMetrics}
          <button
            type="button"
            className={styles.editQuantityButton}
            onClick={(e) => {
              e.stopPropagation();
              onEditQuantity(rowData, address);
            }}
            aria-label={`Editar cantidad en ${address.name}`}
          >
            <Edit2 className={styles.editQuantityIcon} />
          </button>
        </span>
      );
    },
  }));
}

export interface ProductsTableProps {
  searchQuery: string;
  onPageChange?: (page: number) => void;
}

export function ProductsTable({
  searchQuery,
  onPageChange,
}: ProductsTableProps) {
  const history = useHistory();
  const filter = useFilterStore((state) => state.filter);
  const [currentPage, setCurrentPage] = useState(1);
  const inventoryOffset = (currentPage - 1) * INVENTORY_PAGE_SIZE;
  // Inventory hook — always active
  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
    refetch: refetchInventory,
  } = useGetInventory({
    search: searchQuery || undefined,
    limit: INVENTORY_PAGE_SIZE,
    offset: inventoryOffset,
  });

  const { mutateAsync: executeTransfer } = useTransferStock();
  const { mutateAsync: executeDelete } = useDeleteProduct();
  const { mutateAsync: executeUpdatePrice } = useUpdateProductPrice();
  const { mutateAsync: executeUpdateStock } = useUpdateProductStock();

  // RBAC — permission gates
  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();

  const canTransfer =
    !permissionsLoading && permissions.has("inventory:transfer");
  const canEditProduct =
    !permissionsLoading && permissions.has("inventory:edit_product");
  const canDeleteProduct =
    !permissionsLoading && permissions.has("inventory:delete_product");
  const canEditValue =
    !permissionsLoading && permissions.has("inventory:edit_value");
  const canEditQuantity =
    !permissionsLoading && permissions.has("inventory:edit_quantity");

  // State for ProductDetails side panel
  const [selectedProduct, setSelectedProduct] = useState<SalesProduct | null>(
    null
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // State for StockTransferModal
  const [transferProduct, setTransferProduct] =
    useState<InventoryTableRow | null>(null);
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  // State for TransferHistoryModal
  const [historyProduct, setHistoryProduct] =
    useState<InventoryTableRow | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // State for delete confirmation
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  // State for inline quantity edit (T5.4)
  // null  = no cell being edited
  // { productId, addressId, value } = cell open for editing
  const [quantityEdit, setQuantityEdit] = useState<{
    productId: number;
    addressId: number;
    value: string;
  } | null>(null);

  /** Products prepared for the inventory multi-location table */
  const inventoryTableData = useMemo<InventoryTableRow[]>(() => {
    if (!inventoryData?.products) return [];
    return inventoryData.products.map((product) => {
      const unitsAvailable = getInventoryTotals(
        product.stockByLocation
      ).availableStock;
      return {
        ...product,
        units_available: unitsAvailable,
        status: getProductStatus(unitsAvailable),
      };
    });
  }, [inventoryData]);

  const storageAddresses = useMemo<StorageAddressRef[]>(
    () => inventoryData?.storageAddresses ?? [],
    [inventoryData]
  );

  // T5.4 — warehouse quantity inline edit handlers (defined before useMemo that uses them)
  const submitQuantityEdit = useCallback(async () => {
    if (!quantityEdit) return;
    const parsed = Number.parseInt(quantityEdit.value, 10);
    if (!Number.isInteger(parsed) || parsed < 0) {
      errorToast("La cantidad debe ser un entero no negativo");
      return;
    }
    const payload: UpdateStockPayload = {
      product_id: quantityEdit.productId,
      storage_address_id: quantityEdit.addressId,
      quantity: parsed,
    };
    try {
      await executeUpdateStock(payload);
      refetchInventory();
      successToast("Cantidad actualizada exitosamente");
      setQuantityEdit(null);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al actualizar la cantidad";
      errorToast(message);
    }
  }, [quantityEdit, executeUpdateStock, refetchInventory]);

  const cancelQuantityEdit = useCallback(() => setQuantityEdit(null), []);

  /** Dynamic columns built from the list of storage addresses */
  const inventoryColumns = useMemo(
    () =>
      buildInventoryColumns(storageAddresses, {
        canEditQuantity,
        editingCell: quantityEdit,
        onEditQuantity: (product, address) => {
          const stock = product.stockByLocation.find(
            (s) => s.storage_address_id === address.id
          );
          setQuantityEdit({
            productId: product.id,
            addressId: address.id,
            value: String(stock?.quantity ?? 0),
          });
        },
        onChangeQuantity: (value) =>
          setQuantityEdit((prev) => (prev ? { ...prev, value } : prev)),
        onSubmitQuantity: () => {
          void submitQuantityEdit();
        },
        onCancelQuantity: () => cancelQuantityEdit(),
      }),
    [storageAddresses, canEditQuantity, quantityEdit, submitQuantityEdit, cancelQuantityEdit]
  );

  const handleTransfer = async (payload: TransferPayload) => {
    try {
      await executeTransfer(payload);
      refetchInventory();
      setIsTransferOpen(false);
      successToast("Stock trasladado exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al trasladar el stock";
      errorToast(message);
    }
  };

  // T6.1 — unit_price cell inline edit (Material Table cellEditable)
  const handlePriceEdit = async (
    newPrice: number,
    productId: number
  ): Promise<void> => {
    const payload: UpdatePricePayload = { unit_price: newPrice };
    try {
      await executeUpdatePrice({ productId, payload });
      refetchInventory();
      successToast("Precio actualizado exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al actualizar el precio";
      errorToast(message);
      // Re-throw so MaterialTable reverts the cell to the old value
      throw err;
    }
  };

  const handleDelete = async (productId: number) => {
    try {
      await executeDelete(productId);
      setDeleteConfirmDialog({ ...deleteConfirmDialog, isOpen: false });
      successToast("Producto eliminado exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al eliminar el producto";
      errorToast(message);
    }
  };

  // Note: dep is `inventoryTableData` (not `inventoryData?.products` as the original
  // design suggested). `inventoryTableData` is the *actual input* to the filter — using
  // `inventoryData?.products` here would create a stale closure: if the data wrapper
  // ref changes while the products array reference stays stable, the filter would skip
  // re-running and the captured `inventoryTableData` would lag one render behind.
  const filteredInventoryData = useMemo(
    () => filterInventoryProducts(inventoryTableData, searchQuery, filter),
    [inventoryTableData, searchQuery, filter]
  );

  const baseColumns: Column<InventoryTableRow>[] = [
    {
      title: "Estado",
      field: "status",
      editable: "never",
      render: (rowData) => {
        const { variant, label } = getStatusBadge(rowData.status);
        return <Badge variant={variant}>{label}</Badge>;
      },
      cellStyle: { whiteSpace: "nowrap" },
    },
    {
      title: "Línea",
      field: "line",
      editable: "never",
      render: (rowData) => (
        <span className={styles.lineBadge}>{rowData.line}</span>
      ),
      cellStyle: { whiteSpace: "nowrap" },
    },
    {
      title: "Nombre",
      field: "name",
      editable: "never",
      render: (rowData) => (
        <div>
          <div className={styles.productInfo}>{rowData.name}</div>
          <div className={styles.productId}>ID: {rowData.id}</div>
        </div>
      ),
    },
    {
      title: "Precio sin IVA",
      field: "price",
      type: "numeric",
      // T6.1: price column is now display-only; inline edit targets unit_price
      editable: "never",
      render: (rowData) => (
        <span className={styles.stockCell}>
          <span>{formatNumberToPrice(rowData.price)}</span>
        </span>
      ),
    },
    {
      title: "Precio Unitario",
      field: "unit_price",
      type: "numeric",
      // T6.1: editable when user has inventory:edit_value
      editable: canEditValue ? "always" : "never",
      render: (rowData) => (
        <span className={styles.stockCell}>
          <span>
            {rowData.unit_price != null
              ? formatNumberToPrice(rowData.unit_price)
              : "—"}
          </span>
          {canEditValue && (
            <span className={styles.editQuantityButton}>
              <Edit2 className={styles.editQuantityIcon} />
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Stock físico",
      field: "physical_stock",
      type: "numeric",
      editable: "never",
      render: (rowData) =>
        getInventoryTotals(rowData.stockByLocation).physicalStock,
    },
    {
      title: "Stock reservado",
      field: "reserved_stock",
      type: "numeric",
      editable: "never",
      render: (rowData) =>
        getInventoryTotals(rowData.stockByLocation).reservedStock,
    },
    {
      title: "Total disponible",
      field: "units_available",
      type: "numeric",
      editable: "never",
      render: (rowData) => (
        <span
          className={`${styles.stockText} ${
            rowData.units_available === 0
              ? styles.stockZero
              : rowData.units_available < 10
              ? styles.stockLow
              : styles.stockNormal
          }`}
        >
          {rowData.units_available}
        </span>
      ),
    },
  ];

  const actionsColumn: Column<InventoryTableRow> = {
    title: "",
    field: "actions",
    editable: "never",
    sorting: false,
    cellStyle: { whiteSpace: "nowrap", width: "1%" },
    render: (product) => (
      <RowActionsMenu
        items={[
          {
            label: "Ver detalle",
            icon: <Eye className={styles.actionIcon} />,
            onClick: () => {
              // Map InventoryTableRow to SalesProduct shape for ProductDetails
              setSelectedProduct({
                id: String(product.id),
                name: product.name,
                line: product.line,
                price: product.price,
                units_available: product.units_available,
                status: product.status,
                widthCm: product.widthCm ?? null,
                lengthCm: product.lengthCm ?? null,
                areaM2: product.areaM2 ?? null,
                warehouseStock: 0,
                freeZoneStock: 0,
                imageUrl: product.imageUrl ?? undefined,
                brand: product.brand ?? null,
                complementaries: product.complementaries,
              } as SalesProduct);
              setIsDetailsOpen(true);
            },
          },
          ...(canTransfer
            ? [
                {
                  label: "Trasladar stock",
                  icon: <ArrowLeftRight className={styles.actionIcon} />,
                  onClick: () => {
                    setTransferProduct(product);
                    setIsTransferOpen(true);
                  },
                },
                {
                  label: "Historial de traslados",
                  icon: <History className={styles.actionIcon} />,
                  onClick: () => {
                    setHistoryProduct(product);
                    setIsHistoryOpen(true);
                  },
                },
              ]
            : []),
          ...(canEditProduct
            ? [
                {
                  label: "Editar producto",
                  icon: <EditIcon className={styles.actionIcon} />,
                  onClick: () => {
                    history.push(`/proveedor/producto/${product.id}/editar`);
                  },
                },
              ]
            : []),
          ...(canDeleteProduct
            ? [
                {
                  label: "Eliminar producto",
                  icon: <Trash2 className={styles.actionIconDanger} />,
                  danger: true,
                  onClick: () => {
                    setDeleteConfirmDialog({
                      isOpen: true,
                      title: "Eliminar producto",
                      subTitle: `¿Estás seguro que deseas eliminar "${product.name}"? Esta acción se puede deshacer.`,
                      onConfirm: () => handleDelete(product.id),
                    });
                  },
                },
              ]
            : []),
        ]}
      />
    ),
  };

  const allColumns = [actionsColumn, ...baseColumns, ...inventoryColumns];

  const pageSize = inventoryData?.limit || INVENTORY_PAGE_SIZE;
  const totalPages = inventoryData
    ? Math.max(1, Math.ceil((inventoryData.total ?? 0) / pageSize))
    : Math.max(1, currentPage);

  useEffect(() => {
    if (!inventoryData) return;
    setCurrentPage((page) => {
      const clampedPage = Math.min(page, totalPages);
      if (clampedPage !== page) onPageChange?.(clampedPage);
      return clampedPage;
    });
  }, [inventoryData, totalPages, onPageChange]);

  useEffect(() => {
    setCurrentPage((page) => {
      if (page !== 1) onPageChange?.(1);
      return 1;
    });
  }, [searchQuery, onPageChange]);

  const handlePageChange = (page: number) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === currentPage) return;
    setCurrentPage(nextPage);
    onPageChange?.(nextPage);
  };

  return (
    <div className={styles.container}>
      {storageAddresses.length === 0 && !isLoadingInventory && (
        <p className={styles.emptyState}>
          No hay bodegas configuradas. Agrega una dirección de bodega en la
          configuración del proveedor.
        </p>
      )}
      <MaterialTable
        title=""
        columns={allColumns}
        data={filteredInventoryData}
        isLoading={isLoadingInventory}
        cellEditable={
          canEditValue
            ? {
                onCellEditApproved: (
                  newValue: any,
                  _oldValue: any,
                  rowData: InventoryTableRow,
                  _columnDef: any
                ) => {
                  return handlePriceEdit(Number(newValue), rowData.id);
                },
              }
            : undefined
        }
        options={{
          search: false,
          toolbar: false,
          paging: false,
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
            height: "3.5rem",
          },
          rowStyle: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#1C252E",
          },
        }}
        localization={materialTableES}
      />

      <div className={styles.pagination} aria-label="Inventory pagination">
        <button
          type="button"
          aria-label="Previous page"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Anterior
        </button>
        {Array.from({ length: totalPages }, (_, index) => {
          const page = index + 1;
          return (
            <button
              key={page}
              type="button"
              className={styles.pageButton}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? "page" : undefined}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          );
        })}
        <span aria-live="polite">
          Página {currentPage} de {totalPages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Siguiente
        </button>
      </div>

      <ProductDetails
        product={selectedProduct}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
      />

      {isTransferOpen && transferProduct && (
        <StockTransferModal
          open={isTransferOpen}
          product={transferProduct}
          storageAddresses={storageAddresses}
          defaultFromAddressId={storageAddresses[0]?.id ?? 0}
          onClose={() => setIsTransferOpen(false)}
          onTransfer={handleTransfer}
        />
      )}

      {isHistoryOpen && historyProduct && (
        <TransferHistoryModal
          open={isHistoryOpen}
          product={historyProduct}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      <DeleteConfirmationDialog
        confirmDialog={deleteConfirmDialog}
        setConfirmDialog={setDeleteConfirmDialog}
      />
    </div>
  );
}
